// =============================================================================
// LIGA AJUPAM — React.lazy con recuperación de chunks obsoletos SIN ruido en Sentry
// =============================================================================
// Reemplazo directo de React.lazy para los imports dinámicos de la app. Ante un
// redeploy, un usuario con la shell vieja abierta puede pegar:
//   - el import rechaza ("Failed to fetch dynamically imported module"), o
//   - en WebKit/iOS el chunk 404 devuelve el HTML fallback y el import RESUELVE
//     a un módulo `undefined` → `.then(m => ({ default: m.X }))` lanza
//     "undefined is not an object (evaluating 'e.X')".
//
// El sistema previo (ErrorBoundary.onError → reloadForStaleChunk) recupera con
// una recarga, PERO Sentry.ErrorBoundary captura el error ANTES de llamar a
// onError → el error transitorio igual se reporta (ruido).
//
// Manejándolo acá, en el sitio del import, la recarga se dispara antes de que el
// error suba al ErrorBoundary raíz: React queda en <Suspense> con una promesa
// pendiente mientras la página recarga, así el error transitorio NUNCA se reporta.
// Si el guard anti-loop de reloadForStaleChunk decide no recargar (deploy
// realmente roto, persistente), propagamos el error para que sí se reporte.
//
// No todo fallo es un chunk obsoleto: en red móvil inestable el chunk existe y
// sólo falló el fetch. Ahí recargar la app entera es la reacción más cara justo
// cuando la conexión está mal, así que primero REINTENTAMOS el import (ver
// RETRY_DELAYS_MS) y recién si eso no alcanza recurrimos a la recarga.
// =============================================================================

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { isMissingExportError, isOffline, reloadForStaleChunk } from './chunkReload';

const EMPTY_MODULE = 'EMPTY_MODULE';

// Backoff de los reintentos ante un fallo de RED (ms de espera ANTES de cada
// reintento). Tres intentos en total, ~1s de techo: suficiente para cubrir un
// bache de red móvil sin que el usuario perciba más que el spinner de Suspense.
const RETRY_DELAYS_MS = [250, 750];

// Cuánto esperamos a que vuelva la conexión antes de darnos por vencidos, y
// cuántas veces aceptamos volver a esperar. El tope evita el ping-pong infinito
// (vuelve la red → el import falla igual → se cae de nuevo → esperar otra vez).
const OFFLINE_WAIT_MS = 60_000;
const MAX_OFFLINE_WAITS = 2;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Espera a que el navegador vuelva a estar online. Devuelve true si volvió
 * dentro del tope, false si se agotó el tiempo.
 */
function waitForOnline(timeoutMs: number): Promise<boolean> {
  if (!isOffline()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (back: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      resolve(back);
    };
    const onOnline = () => finish(true);
    const timer = setTimeout(() => finish(false), timeoutMs);
    window.addEventListener('online', onOnline);
  });
}

/** Un intento de carga. Normaliza el módulo vacío de WebKit a un rechazo. */
async function loadModule<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
  const mod = await factory();
  // WebKit/iOS: el import puede resolver un módulo vacío/undefined cuando el
  // chunk 404 devuelve el HTML fallback. Tratarlo como chunk obsoleto.
  if (!mod || (mod as { default?: unknown }).default == null) {
    throw new Error(EMPTY_MODULE);
  }
  return mod;
}

/**
 * ¿Este fallo significa que el chunk NO existe (o sea, que reintentar es inútil
 * y hay que recargar para tomar assets nuevos)?
 *
 * v6.11.143 — La comprobación de `loadModule` de acá arriba NO alcanzaba, y de
 * hecho era código muerto para casi todos los call sites. El factory que llega
 * no es el `import()` pelado: es
 *
 *     () => import('./components/public/PublicRouter').then(m => ({ default: m.PublicRouter }))
 *
 * o sea que el `.then` corre DENTRO del factory, ANTES de que `loadModule` pueda
 * mirar el módulo. Cuando el chunk 404 y el host devuelve el HTML fallback, el
 * import resuelve `undefined` y ese `.then` explota primero con
 * "Cannot read properties of undefined (reading 'PublicRouter')" — un TypeError,
 * no EMPTY_MODULE. De los 48 call sites, 40 llevan ese `.then`: los únicos que
 * llegaban a la comprobación eran los 8 que importan un `default` directo
 * (`import('./pages/ReceptionistPage')`).
 *
 * La consecuencia era que un chunk obsoleto se clasificaba como fallo de RED:
 * dos reintentos de un archivo que ya no existe (~1s de espera garantizada
 * inútil) antes de la recarga que sí lo arregla. Y si el guard anti-loop se
 * rendía, terminaba en Sentry con cara de bug de la app (LIGA-AJUPAM-4T).
 *
 * `isMissingExportError` reconoce esa forma. Se clasifica por el error y no por
 * el módulo a propósito: así vale para los dos call sites (con `.then` y sin) y
 * el error ORIGINAL se sigue propagando tal cual, que es lo que le dice a quien
 * triagea CUÁL chunk faltó — reescribirlo a EMPTY_MODULE perdía ese dato.
 */
function isEmptyModuleFailure(err: unknown): boolean {
  return (err as { message?: string })?.message === EMPTY_MODULE || isMissingExportError(err);
}

/**
 * React.lazy con recuperación en tres niveles, según el modo de falla:
 *   - SIN CONEXIÓN → no es un chunk obsoleto, es el celular sin señal. Recargar
 *     acá tira abajo la app que ya estaba andando y la recarga tampoco va a
 *     poder traer nada. Esperamos a que vuelva la red y reintentamos; React
 *     sigue en <Suspense> mientras tanto.
 *   - RECHAZO del import (con conexión) → la red falló igual (móvil inestable,
 *     bache puntual). El chunk existe: reintentar lo resuelve sin recargar.
 *   - EMPTY_MODULE → el chunk 404 y el host devolvió el HTML fallback (WebKit
 *     resuelve un módulo vacío en vez de rechazar). El archivo NO existe:
 *     reintentar es inútil, hay que tomar assets nuevos → recarga directa.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    let attempt = 0;
    let offlineWaits = 0;
    for (;;) {
      try {
        return await loadModule(factory);
      } catch (err) {
        const isEmptyModule = isEmptyModuleFailure(err);
        // Fallo de red y quedan reintentos → esperar y volver a pedir el chunk.
        // React sigue en <Suspense> mientras tanto (spinner, sin pantalla de error).
        if (!isEmptyModule && attempt < RETRY_DELAYS_MS.length) {
          await sleep(RETRY_DELAYS_MS[attempt]);
          attempt++;
          continue;
        }
        // v6.11.67 — Sin conexión: esperar a que vuelva en vez de gastar recargas.
        // Antes esto caía en reloadForStaleChunk y, en una red móvil mala, los
        // tres fallos entraban en la ventana de 30s del guard: dos recargas
        // inútiles y pantalla de error, reportada a Sentry como si fuera un bug.
        if (isOffline() && offlineWaits < MAX_OFFLINE_WAITS) {
          offlineWaits++;
          if (await waitForOnline(OFFLINE_WAIT_MS)) {
            attempt = 0; // volvió la red: el presupuesto de reintentos se renueva
            continue;
          }
          // Se agotó la espera y sigue sin conexión → propagar. No se recarga y
          // beforeSend lo descarta: no es un error de la app, es el subte.
          throw err;
        }
        // Chunk obsoleto (o red caída de forma persistente) → recargar para tomar
        // assets nuevos (con el guard anti-loop compartido).
        if (reloadForStaleChunk('lazyImport')) {
          // Promesa pendiente: React queda en Suspense mientras la página recarga.
          return await new Promise<{ default: T }>(() => {});
        }
        // Persistente (el guard no recargó) → propagar para que se reporte de verdad.
        throw err;
      }
    }
  });
}
